import { inject, Injectable } from '@angular/core';
import {
	deleteObject,
	FullMetadata,
	getDownloadURL,
	getMetadata,
	listAll,
	ref,
	Storage,
	uploadBytes,
	UploadMetadata,
} from '@angular/fire/storage';
import { IStorageFile } from '@shared/models/storage';

@Injectable({
	providedIn: 'root',
})
export class StorageService {
	protected storage = inject(Storage);

	/**
	 * Uploads a file to Firebase Storage.
	 * @param path - The storage path where the file should be uploaded.
	 * @param file - The file to be uploaded.
	 * @returns A promise that resolves with the download URL of the uploaded file.
	 */
	protected async uploadFile(
		path: string,
		file: File | Blob,
		customMetadata?: {
			[key: string]: string;
		},
	): Promise<string> {
		const storageRef = ref(this.storage, path);
		const metadata: UploadMetadata = {
			customMetadata,
			contentType: file.type,
			cacheControl: 'public, max-age=31536000',
		};
		await uploadBytes(storageRef, file, metadata);
		return await getDownloadURL(storageRef);
	}

	/**
	 * Fetches a file from Firebase Storage.
	 * @param path - The storage path where the file is located.
	 * @returns A promise that resolves with the download URL of the uploaded file.
	 */
	async getFileUrl(path: string): Promise<string> {
		const storageRef = ref(this.storage, path);
		return await getDownloadURL(storageRef);
	}

	/**
	 * Fetches a file's meta data from Firebase Storage.
	 * @param path - The storage path where the file is located.
	 * @returns A promise that resolves with the file's meta data.
	 */
	async getFileMetaData(path: string): Promise<FullMetadata> {
		const storageRef = ref(this.storage, path);
		return await getMetadata(storageRef);
	}

	/**
	 * Fetches a file to Firebase Storage.
	 * @param path - The storage path where the file should be uploaded.
	 * @returns A promise that resolves with an array of file pathes in the folder.
	 */
	async listFilesInFolder(path: string): Promise<string[]> {
		const folderRef = ref(this.storage, path);
		const folderContents = await listAll(folderRef);
		return folderContents.items.map((item) => item.fullPath);
	}

	/**
	 * Lists all files in a folder along with their metadata.
	 * @param path - The storage path of the folder.
	 * @returns A promise that resolves with an array of file metadata objects.
	 */
	async listFilesInFolderWithMetadata(path: string): Promise<Array<IStorageFile>> {
		const folderRef = ref(this.storage, path);
		const folderContents = await listAll(folderRef);

		const filesWithMetadata = await Promise.all(
			folderContents.items.map(async (item) => {
				const [metadata, downloadUrl] = await Promise.all([getMetadata(item), getDownloadURL(item)]);
				return {
					...metadata,
					downloadUrl,
				};
			}),
		);

		return filesWithMetadata;
	}

	async deleteFile(path: string): Promise<void> {
		const fileRef = ref(this.storage, path);
		return await deleteObject(fileRef);
	}
}
